import { AgGridReact } from "ag-grid-react";
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";

import { CellEditingStoppedEvent, ColDef, ColumnState, ICellRendererParams, PostSortRowsParams, RowClickedEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import { defaultPRD3020_R01Dto, PRD3020_R01Dto } from "./Dto_PRD3020_R01";
import { usePRD3020_R01Store } from "./Store_PRD3020_R01";
import { getItemPopupList } from "src/apis/itemApi";
import { formatDowntimeMinutes, getDowntimeMinutes, headerNames_PRD3020_R01, isDuplicateChildItemcd, restoreOriginalValues, validateItemAccount } from './Util_PRD3020_R01';
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/useDetailTableEvents";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { getMenuComponent } from "src/utils/menu/menuUtil";
import { changeWorkplaceColumn, CommonCodeColumn, CompCdColumn, getCommonCodeColumnOptions, getCompCdColumnOptions, getWorkPlaceColumnOptions, WorkPlaceColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	// hoveredRowIndex: number | null;
	// setHoveredRowIndex: (hoveredRowIndex: number | null) => void;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<PRD3020_R01Dto> | null>;
	defaultPRD3020_R01Row: PRD3020_R01Dto;
	fetchOne: (data: PRD3020_R01Dto ) => void;
}

const COLUMN_STATE_KEY = 'PRD3020_R01-grid-column-state';

export const Table_PRD3020_R01 = (props: Props) => {

	const { 
		// hoveredRowIndex, setHoveredRowIndex,
		clickable, setClickable,
		gridRef,
		defaultPRD3020_R01Row,
		fetchOne,
	} = props;

    const { 
		PRD3020_R01List, setPRD3020_R01List,
		PRD3020_R01DetailList,
		insertPRD3020_R01List,
		removePRD3020_R01List,
		updatePRD3020_R01List,
		selectedPRD3020_R01, setSelectedPRD3020_R01, initSelectedPRD3020_R01,
		deletedPRD3020_R01List, removeDeletedPRD3020_R01List, addDeletedPRD3020_R01List,
        columnState, setColumnState,
		scrollTop, setScrollTop,
		originalPRD3020_R01List, setOriginalPRD3020_R01List,
		modalVisible, setModalVisible,
		isDisabled,
	} = usePRD3020_R01Store();
	const { setMessage } = useUiuxStore();

    const { compCdList, jobTeamList, orgCdList, ordTypeList, workProcessList, workPlaceList, unitList } = useCommonCodeStore();

	// const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    //const gridRef = useRef<AgGridReact>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent<PRD3020_R01Dto>({
		gridRef,
		key: 'productResultNo',
		gridData: PRD3020_R01List,
		defaultGridData: defaultPRD3020_R01Row,
		selectedRow: selectedPRD3020_R01,
	});

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'productResultNo')(params), []);

	const downtimeMinutesByProductResultNo = useMemo(() => {
		const minutesByProductResultNo = new Map<string, number>();

		PRD3020_R01DetailList.forEach((detail) => {
			if (!detail.productResultNo) return;

			const downtimeMinutes = getDowntimeMinutes(detail.fromTime, detail.toTime) ?? 0;
			minutesByProductResultNo.set(
				detail.productResultNo,
				(minutesByProductResultNo.get(detail.productResultNo) ?? 0) + downtimeMinutes,
			);
		});

		return minutesByProductResultNo;
	}, [PRD3020_R01DetailList]);

	const totalDowntimeMinutes = useMemo(() =>
		PRD3020_R01List.reduce(
			(sum, row) => sum + (downtimeMinutesByProductResultNo.get(row.productResultNo) ?? 0),
			0,
		),
	[PRD3020_R01List, downtimeMinutesByProductResultNo]);

	const baseColumnDefs: ColDef<PRD3020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '비가동설정', colId: 'downtimeSet', field: 'downtimeSet', width: 100, 
			editable: false,
			cellRenderer: (params: ICellRendererParams<PRD3020_R01Dto>) => {
				// 하단 합계 행(pinned row)인 경우 렌더링 하지 않음
				if (params.node.rowPinned) return null;

				return (
					<div className="w-full h-full flex items-center justify-center">
						<button
							className="w-full h-full flex items-center justify-center bg-gray-300 text-white text-[14px] rounded hover:bg-gray-600 border border-gray-600"
							onClick={(e) => {
								e.stopPropagation(); // 행 클릭 이벤트 전파 방지
								if (!params.data) return;
								setSelectedPRD3020_R01(params.data);
								fetchOne(params.data);
								setModalVisible(true);
							}}
						>
							설정
						</button>
					</div>
				);
			}
		},
		{
			headerName: '생산실적번호', colId: 'productResultNo', field: 'productResultNo', width: 150, 
			cellStyle: (p) => getCellStyle(p, 'productResultNo'),
			editable: false,
		},
		{
			headerName: '작업지시번호', colId: 'productOrderNo', field: 'productOrderNo', width: 150, 
			cellStyle: (p) => getCellStyle(p, 'productOrderNo'),
			editable: false,
		},
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: '작업일자', colId: 'productDt', field: 'productDt', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productDt'),
			valueFormatter: (params: any) => params.data?.productDt ? formatDate(new Date(params.data?.productDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '지시구분', colId: 'orderType', field: 'orderType', width: 100, 
			...CommonCodeColumn.options(ordTypeList, false, undefined, undefined),
		},
		{
			headerName: '작업공정', colId: 'workplaceCd', field: 'workplaceCd', width: 100, 
			cellStyle: (p) => getCellStyle(p, 'workplaceCd'),
			...WorkPlaceColumn.options(workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y'), false, (params) => params.data?.compCd, undefined, () => {}),
		},
		// {
		// 	headerName: '공정유형', colId: 'workProcess', field: 'workProcess', width: 100, 
		// 	cellStyle: (p) => getCellStyle(p, 'workProcess'),
		// 	...CommonCodeColumn.options(workProcessList, false, undefined, undefined),
		// },
		{
			headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam', width: 100, 
			cellStyle: (p) => getCellStyle(p, 'jobTeam'),
			...CommonCodeColumn.options(jobTeamList, false, undefined, undefined),
		},
		{
			headerName: '총 비가동시간', colId: 'totalDowntimeMinutes', width: 120,
			editable: false,
			cellStyle: { textAlign: 'right', justifyContent: 'flex-end' },
			valueGetter: (params) => {
				if (params.node?.rowPinned === 'bottom') return totalDowntimeMinutes;
				if (!params.data?.productResultNo) return null;
				return downtimeMinutesByProductResultNo.get(params.data.productResultNo) ?? 0;
			},
			valueFormatter: (params) =>
				typeof params.value === 'number' ? formatDowntimeMinutes(params.value) : '',
		},
		{
			headerName: '작업자', colId: 'productEmp', field: 'productEmp', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productEmp'),
		},
		{
			headerName: '작업자명', colId: 'productEmpNm', field: 'productEmpNm', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productEmpNm'),
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 100, 
			cellStyle: (p) => getCellStyle(p, 'itemUnit'),
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '수량', colId: 'orderQty', field: 'orderQty', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'orderQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		{
			headerName: '생산량', colId: 'acceptQty', field: 'acceptQty', width: 100, 
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'acceptQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		

		// ...IndexParameterCell(),

	], [getCellStyle, cellDateComparator, compCdList, ordTypeList, workPlaceList, workProcessList, jobTeamList, unitList, fetchOne, setModalVisible, setSelectedPRD3020_R01, downtimeMinutesByProductResultNo, totalDowntimeMinutes]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const migratedColumnState = useMemo<ColumnState[] | null>(() => {
		if (columnState === null || columnState.some((state) => state.colId === 'totalDowntimeMinutes')) {
			return columnState;
		}

		const downtimeColumnState = defaultColumnState.find(
			(state) => state.colId === 'totalDowntimeMinutes',
		);
		if (!downtimeColumnState) return columnState;

		const nextColumnState = [...columnState];
		const jobTeamIndex = nextColumnState.findIndex((state) => state.colId === 'jobTeam');
		nextColumnState.splice(jobTeamIndex >= 0 ? jobTeamIndex + 1 : nextColumnState.length, 0, downtimeColumnState);
		return nextColumnState;
	}, [columnState, defaultColumnState]);

	useEffect(() => {
		if (columnState === null || migratedColumnState === columnState) return;

		localStorage.setItem(COLUMN_STATE_KEY, JSON.stringify(migratedColumnState));
		setColumnState(migratedColumnState);
	}, [columnState, migratedColumnState, setColumnState]);

	const columnDefs: ColDef<PRD3020_R01Dto>[] = useMemo(() => {
		if (migratedColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PRD3020_R01Dto>({
			baseColumnDefs,
			columnState: migratedColumnState,
			headerNames: headerNames_PRD3020_R01,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [migratedColumnState, settingRowIndex, baseColumnDefs]);


	const handleRowClick = useCallback((e: RowClickedEvent<PRD3020_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - salesEstimateNo를 우선 사용
		const currentKey = rowData.productResultNo;
		const selectedKey = selectedPRD3020_R01?.productResultNo;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
			setSelectedPRD3020_R01(undefined);
			setMessage('');
		} else {
			setSelectedPRD3020_R01(rowData);
			const hasDowntimeDetail = PRD3020_R01DetailList.some(
				(detail) => detail.productResultNo === currentKey
			);
			const noDowntimeMessage = '선택한 생산실적의 비가동 등록 내역이 없습니다.';
			setMessage(hasDowntimeDetail ? '' : noDowntimeMessage);
			if (!hasDowntimeDetail) {
				showWarning(noDowntimeMessage);
			}
		}
	}, [PRD3020_R01DetailList, selectedPRD3020_R01, setMessage, setSelectedPRD3020_R01]);
	
	const pinnedBottomRow = useMemo(() => ([
		{
			rowIndex: '계',
			productOrderNo: PRD3020_R01List.length.toLocaleString() + "건",
			orderQty: PRD3020_R01List.reduce((sum, account) => sum + (deletedPRD3020_R01List.includes(account.productResultNo) ? 0 : Number(account.orderQty) || 0), 0).toLocaleString(),
			resultQty: PRD3020_R01List.reduce((sum, account) => sum + (deletedPRD3020_R01List.includes(account.productResultNo) ? 0 : Number(account.productQty) || 0), 0).toLocaleString(),
			editable: false,
		}
	]), [PRD3020_R01List]);

    return (
		<GridContextWrapper<PRD3020_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedPRD3020_R01}
			defaultRowData={defaultPRD3020_R01Row}
			baseColumnDefs={migratedColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<PRD3020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<PRD3020_R01Dto>({
					idKey: 'productResultNo',
					selectedList: selectedPRD3020_R01 ? [selectedPRD3020_R01.productResultNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setColumnState}			//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
